import { useRef, useState } from 'react'

import {
  EMPTY_FUNCTION,
  EMPTY_OBJECT,
  FLOATING_ELEMENT_PLACEMENT,
} from '../../constants/common'
import { CLOSE_ON_ANCESTOR_SCROLL_NEVER } from '../../constants/select'
import { useFloatingSelect } from '../../hooks'
import { FloatingSelectDropdown } from '../Floating'

import {
  EMPTY_SEARCH_CONFIGS,
  EMPTY_SEARCH_VALUE,
  SEARCH_TIMEOUT,
} from './Search.constants'
import { useSearchListValue } from './Search.hooks'
import { setSearchValueArchived, setSearchValueType } from './Search.utils'
import { SearchArchivedInput } from './SearchArchivedInput'
import { SearchDropdown } from './SearchDropdown'
import { SearchList } from './SearchList'
import { SearchSection } from './SearchSection'
import { SearchTextInput } from './SearchTextInput'
import { SearchTypeInput } from './SearchTypeInput'

import * as styles from './Search.module.css'

/**
 * @import {SearchListProps} from "./SearchList/SearchList"
 */

/**
 * @typedef SearchProps
 *
 * @property {React.ComponentType<SearchListProps>} [List]
 *
 * @property {string} [className]
 *
 * @property {SearchConfigs} [configs]
 *
 * @property {SearchValue} [defaultSearch]
 *
 * @property {boolean} [disabled]
 *
 * @property {SearchListProps} [listProps]
 *
 * @property {number} [timeout]
 *
 * @property {(search: SearchValue) => void} [onSearch]
 *
 * @property {(value: SearchItemValue<unknown>) => void} [onSelect]
 */

/**
 * @param {SearchProps} props
 */
function Search({
  List = SearchList,
  className = styles.Search,
  configs = EMPTY_SEARCH_CONFIGS,
  defaultSearch = EMPTY_SEARCH_VALUE,
  listProps = EMPTY_OBJECT,
  timeout = SEARCH_TIMEOUT,
  onSearch = EMPTY_FUNCTION,
  onSelect = EMPTY_FUNCTION,
}) {
  const [search, setSearch] = useState(defaultSearch)

  const textInputRef = useRef(null)

  const listValue = useSearchListValue(
    search,
    configs.get(search.type),
    timeout
  )

  const { floatingStyles, getFloatingProps, isOpen, refs, setOpen } =
    useFloatingSelect({
      closeOnAncestorScroll: CLOSE_ON_ANCESTOR_SCROLL_NEVER,
      maxWidthByRef: true,
      placement: FLOATING_ELEMENT_PLACEMENT.bottomStart,
    })

  function handleTextInputFocus() {
    setOpen(true)
  }

  const typeInputItems = [...configs.keys()]

  function handleTypeInputChange(nextType) {
    const typeValue = setSearchValueType(search, nextType)
    const nextValue =
      typeValue === search ? search : setSearchValueArchived(typeValue, false)

    textInputRef.current?.focus()

    if (nextValue !== search) {
      setSearch(nextValue)
    }
  }

  function handleArchivedInputChange(archived) {
    const nextValue = setSearchValueArchived(search, archived)

    textInputRef.current?.focus()

    if (nextValue !== search) {
      setSearch(nextValue)
    }
  }
  const { setReference } = refs

  return (
    <div className={className} ref={setReference}>
      <SearchTextInput
        ref={textInputRef}
        value={search}
        onChange={setSearch}
        onFocus={handleTextInputFocus}
        onSearch={onSearch}
      />

      <FloatingSelectDropdown
        floatingStyles={floatingStyles}
        getFloatingProps={getFloatingProps}
        isOpen={isOpen}
        refs={refs}
      >
        <SearchDropdown className={styles.dropdown}>
          <SearchSection>
            <SearchTypeInput
              className={styles.typeInput}
              items={typeInputItems}
              value={search.type}
              onChange={handleTypeInputChange}
            />
          </SearchSection>

          {search.type?.withArchivedSearch ? (
            <SearchSection>
              <SearchArchivedInput
                className={styles.archivedInput}
                value={search.archived}
                onChange={handleArchivedInputChange}
              />
            </SearchSection>
          ) : null}

          {listValue ? (
            <List
              {...listProps}
              value={listValue}
              onSearch={onSearch}
              onSelect={onSelect}
            />
          ) : null}
        </SearchDropdown>
      </FloatingSelectDropdown>
    </div>
  )
}

export default Search
